import React from "react";
import { motion } from "framer-motion";
import { PhoneGuestMockup } from "./DeviceMockups";
import { CheckCircle2 } from "lucide-react";

const steps = [
  { t: "Booking", d: "Guest books the room." },
  { t: "Pre check-in", d: "Guest completes ID + preferences on mobile." },
  { t: "Hotel verifies", d: "Team validates in HOP." },
  { t: "Room assigned", d: "Perfect match, no queues." },
  { t: "Room number sent", d: "Delivered to guest instantly." },
  { t: "Walk to room", d: "Digital key unlocks the door." },
];

const benefits = [
  "No waiting",
  "Less paperwork",
  "Faster arrival",
  "Better guest experience",
  "Reduced front desk workload",
  "Peak-hour optimization",
];

export default function PreCheckIn() {
  return (
    <section
      id="pre-checkin"
      className="relative py-24 md:py-36 overflow-hidden"
      data-testid="pre-checkin-section"
    >
      {/* BG - purple gradient */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-purpleDeep via-brand-purple to-brand-purpleMid" />
        <div className="absolute -top-20 -right-40 w-[600px] h-[600px] rounded-full bg-brand-orange/20 blur-3xl" />
        <div className="noise-overlay opacity-60" />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="mb-6 inline-flex items-center gap-3 font-display text-[11px] uppercase tracking-[0.28em] text-white/90 font-bold">
            <span className="inline-block w-6 h-px bg-brand-orange" />
            Pre check-in
          </div>
          <h2 className="font-display text-white text-[38px] md:text-[64px] leading-[0.98] tracking-tight text-balance" style={{ fontWeight: 800 }}>
            Arrive.{" "}
            <span className="grad-text">Skip the queue.</span>{" "}
            Go straight to your room.
          </h2>
          <p className="mt-6 text-white/85 max-w-xl">
            A digital-first arrival experience that removes the friction of
            check-in — for the guest, and for your front desk during peak
            hours.
          </p>

          {/* Journey timeline */}
          <ol className="mt-10 relative border-l border-white/25 pl-6 space-y-6">
            {steps.map((s, i) => (
              <motion.li
                key={s.t}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative"
                data-testid={`pre-checkin-step-${i + 1}`}
              >
                <span className="absolute -left-[31px] top-1.5 w-2.5 h-2.5 rounded-full bg-brand-orange shadow-[0_0_18px_rgba(209,79,42,0.7)]" />
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-[10px] uppercase tracking-widest text-white/70 font-bold">
                    Step 0{i + 1}
                  </span>
                  <span className="font-display text-white text-[19px]" style={{ fontWeight: 700 }}>
                    {s.t}
                  </span>
                </div>
                <div className="text-white/75 text-[14px] mt-1">{s.d}</div>
              </motion.li>
            ))}
          </ol>

          {/* Benefits */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-2.5">
            {benefits.map((b) => (
              <div
                key={b}
                className="flex items-center gap-2 text-white/85 text-[14px]"
              >
                <CheckCircle2 size={14} className="text-brand-orange" />
                {b}
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 flex justify-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="animate-float"
          >
            <PhoneGuestMockup className="w-[280px] md:w-[320px]" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
